import type { FC } from 'react';

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { formatLocaleNumber } from '../i18n/locale';
import { AgentSnapshot } from '../features/manage-agents/ui/AgentSnapshot';
import { cn } from '../lib/utils';

export interface DisplayPosition {
  symbol: string;
  sideLabel: string;
  quantity: number;
  notional: number;
  change: number;
  positive: boolean;
}

export interface RightSidebarProps {
  agentId: string;
  portfolioData: { balance: number; dailyPnL: number; marginUsed: number };
  displayPositions: DisplayPosition[];
  portfolioLoading: boolean;
  portfolioError: Error | null;
  onRefetchPortfolio: () => void;
}

export const RightSidebar: FC<RightSidebarProps> = ({
  agentId,
  portfolioData,
  displayPositions,
  portfolioLoading,
  portfolioError,
  onRefetchPortfolio,
}) => {
  const { t } = useTranslation(['common', 'layout']);
  const noAgent = !agentId;

  return (
    <div className="p-4">
      <div className="space-y-4">
        {/* Account Summary */}
        <div className="p-4 bg-card rounded-lg border border-border">
          <h3 className="text-sm font-semibold mb-3">{t('layout:rightSidebar.account')}</h3>
          {noAgent ? (
            <p className="text-xs text-muted-foreground">
              {t('layout:rightSidebar.noAgent')}{' '}
              <Link to="/agents" className="text-primary hover:underline">
                {t('common:actions.goToAgents')}
              </Link>
            </p>
          ) : portfolioLoading ? (
            <div className="space-y-2 text-sm text-muted-foreground">{t('common:states.loading')}</div>
          ) : portfolioError ? (
            <div className="space-y-2 text-xs">
              <p className="text-destructive">{t('layout:rightSidebar.loadAccountFailed')}</p>
              <button
                type="button"
                onClick={onRefetchPortfolio}
                className="text-primary hover:underline"
              >
                {t('common:actions.retry')}
              </button>
            </div>
          ) : (
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('layout:rightSidebar.balance')}</span>
                <span className="font-mono">
                  {formatLocaleNumber(portfolioData.balance, {
                    maximumFractionDigits: 8,
                    minimumFractionDigits: 2,
                  })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('layout:rightSidebar.dailyPnl')}</span>
                <span
                  className={cn(
                    'font-mono',
                    portfolioData.dailyPnL >= 0 ? 'text-green-500' : 'text-red-500'
                  )}
                >
                  {portfolioData.dailyPnL >= 0 ? '+' : ''}
                  {formatLocaleNumber(portfolioData.dailyPnL, {
                    maximumFractionDigits: 8,
                    minimumFractionDigits: 2,
                  })}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('layout:rightSidebar.margin')}</span>
                <span className="font-mono">
                  {formatLocaleNumber(portfolioData.marginUsed, {
                    maximumFractionDigits: 8,
                    minimumFractionDigits: 2,
                  })}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Active Positions */}
        <div className="p-4 bg-card rounded-lg border border-border">
          <h3 className="text-sm font-semibold mb-3">{t('layout:rightSidebar.positions')}</h3>
          <div className="space-y-2">
            {noAgent ? (
              <p className="text-xs text-muted-foreground">{t('layout:rightSidebar.selectAgent')}</p>
            ) : portfolioLoading ? (
              <p className="text-xs text-muted-foreground">{t('common:states.loading')}</p>
            ) : portfolioError ? (
              <p className="text-xs text-destructive">{t('layout:rightSidebar.loadPositionsFailed')}</p>
            ) : displayPositions.length === 0 ? (
              <p className="text-xs text-muted-foreground">{t('layout:rightSidebar.noPositions')}</p>
            ) : (
              <>
                {displayPositions.map((pos: DisplayPosition, idx: number) => (
                  <div key={`${pos.symbol}-${pos.sideLabel}-${idx}`} className="p-2 bg-accent/50 rounded">
                    <div className="flex justify-between text-xs">
                      <span className="font-medium">{pos.symbol}</span>
                      <span
                        className={cn(
                          pos.positive ? 'text-green-500' : 'text-red-500'
                        )}
                      >
                        {pos.positive ? '+' : ''}
                        {formatLocaleNumber(pos.change, {
                          maximumFractionDigits: 8,
                        })}
                      </span>
                    </div>
                    <div className="flex justify-between text-xs text-muted-foreground mt-1">
                      <span>
                        {pos.sideLabel}{' '}
                        {formatLocaleNumber(pos.quantity, {
                          maximumFractionDigits: 6,
                        })}
                      </span>
                      <span>
                        {formatLocaleNumber(pos.notional, {
                          maximumFractionDigits: 2,
                        })}
                      </span>
                    </div>
                  </div>
                ))}
                {displayPositions.length > 0 && (
                  <Link
                    to="/trading"
                    className="block text-xs text-primary hover:underline mt-2"
                  >
                    {t('layout:rightSidebar.openAll')} →
                  </Link>
                )}
              </>
            )}
          </div>
        </div>

        <div className="bg-card rounded-lg border border-border overflow-hidden">
          <AgentSnapshot agentId={agentId} />
        </div>
      </div>
    </div>
  );
};
